import { addClasses, queryAll, readDataset, removeClasses, toggleClasses, touchElement } from 'lambda-dom'
import videojs from 'video.js/dist/alt/video.core.novtt'
import EventDelegation from '@jjwesterkamp/event-delegation'
import { Dropdown } from '@app-components/dropdown/dropdown.component'
import { defeatShadow as defeatMobileShadow } from '@app-services/mobile-header/mobile-header.service'
import { bugsnagClient } from '@app-bootstrap/bugsnag.bootstrap'
import { LegacyRatingWidget } from './components/rating-widget.component'
import {
    createStickyRightSidebarComponent,
    getAlgoliaEventDataFromLocalStorage,
    sendAlgoliaEvent,
} from './catalog-detail.lib'

import { makeThreedViewer } from './components/threed-viewer.component'
import { StickyBar } from './components/sticky-bar.component'
import { BasicSwiper } from '@app-components'
import { Zoomer } from '@app-components/zoomer/Zoomer'
import { ZoomistOptions } from 'zoomist/types'
import { swiperFactoryInjector } from '@app-injections'

LegacyRatingWidget.patchWindow()

// ------------------------------------------------------------------------------
//      Catalog -> Product: Inline Carousel
// ------------------------------------------------------------------------------

const pdpCarousel = document.querySelector('.product-carousel') as HTMLElement
const swiperElement = pdpCarousel?.querySelector('.pdp-swiper') as HTMLElement
const swiperOptions = JSON.parse(swiperElement.dataset.swiperOptions || '{}')

const mainSwiper: BasicSwiper = swiperFactoryInjector.createInstanceWithThumbnails(swiperElement, swiperOptions)

// ------------------------------------------------------------------------------
//      Catalog -> Product: Sticky Sidebar
// ------------------------------------------------------------------------------

try {
    touchElement('#sticky-right-sidebar', createStickyRightSidebarComponent)
    touchElement('#catalog-detail-variant-switch', (element: HTMLSelectElement) => {
        const dropdown = Dropdown.create(element)
        dropdown.choices$.subscribe((choice) => {
            if (choice.value) {
                window.location.href = choice.value
            }
        })
    })

    const stickyBar = StickyBar.query('.cart-action .add-buyable-form', '.sticky-bar')
    stickyBar.isSticky$.subscribe((isSticky: boolean) => {
        defeatMobileShadow(isSticky)
    })
} catch (e) {
    // ... fail silently for now
}

// ------------------------------------------------------------------------------
//      Catalog -> Product: Carousel zoom feature
// ------------------------------------------------------------------------------

const trigger = document.getElementById('product-carousel--zoom-trigger') as HTMLElement | null
const zoomistContainer = document.querySelector('.zoomist-container') as HTMLElement | null

const options: ZoomistOptions = {
    draggable: true,
    wheelable: true,
    pinchable: true,
    bounds: true,
    zoomRatio: 0.1,
    minScale: 1,
    initScale: 1.1,
    maxScale: 3,
    zoomer: true,
}

let zoomer: Zoomer | undefined

if (trigger && zoomistContainer) {
    zoomer = new Zoomer(trigger, getCurrentSlide(), options, zoomistContainer)
}

mainSwiper.swiper.on('slideChange', () => {
    zoomer?.setTarget(getCurrentSlide())
    zoomer?.updateZoomerVisibility(false)
    zoomer?.setupTouchZoom()
})

/**
 * Retrieves the current slide from the carousel.
 * @returns The selector of the current slide or null if not found.
 */
function getCurrentSlide(): HTMLImageElement | null {
    const sliderIndex = mainSwiper?.swiper?.activeIndex
    return mainSwiper?.swiper?.slides[sliderIndex].querySelector('img') as HTMLImageElement ?? null
}

// ------------------------------------------------------------------------------
//      Catalog -> Product: All reviews visibility toggle
// ------------------------------------------------------------------------------
EventDelegation
    .global()
    .events('click')
    .select('button#toggle-all-reviews')
    .listen(() => {
        touchElement('#all-reviews', toggleClasses('hidden'))
        touchElement('button#toggle-all-reviews', addClasses('hidden'))
    })

queryAll('.video-slide video').forEach((videoEl) => {
    // const isInline = videoEl.id.indexOf('in-page') >= 0
    const vjsEl = videojs(videoEl.id, {
        inactivityTimeout: 0,
        controls: true,
        controlBar: {
            children: [
                'PlayToggle',
                'CurrentTimeDisplay',
                'ProgressControl',
                'VolumePanel',
                'FullscreenToggle',
            ],
        } as any,
    }, () => {
        mainSwiper.swiper.on('slideChange', () => {
            vjsEl.pause()
        })
    })
})

// ------------------------------------------------------------------------------
//      Read more button Click event handler
// ------------------------------------------------------------------------------
EventDelegation
    .global()
    .events('click')
    .select('button#catalog-detail-description-toggle')
    .listen((e) => {
        addClasses('hide')(e.delegator)
        touchElement('.catalog-detail--description', removeClasses('clipped'))
    })

// ------------------------------------------------------------------------------
//      Mobile accordion like navigation toggle -- Close by menu title click
// ------------------------------------------------------------------------------
EventDelegation
    .within('div#catalog-detail-container')
    .events('click')
    .select<HTMLElement>('.section-title')
    .listen((event) => {
        const section = readDataset('section')(event.delegator)

        if (section) {
            if (event.delegator.classList.contains('expanded')) {
                touchElement(`[data-section-wrapper="${section}"]`, addClasses('hide-for-small-only'), event.currentTarget)
                queryAll(`.section-title[data-section="${section}"]`, event.currentTarget).forEach(removeClasses('expanded'))
            } else {
                touchElement(`[data-section-wrapper="${section}"]`, removeClasses('hide-for-small-only'), event.currentTarget)
                addClasses('expanded')(event.delegator)
            }
        }
    })

// ------------------------------------------------------------------------------
//      3D viewer
// ------------------------------------------------------------------------------

touchElement('meta[name="sku"]', (skuMetaTag: HTMLMetaElement) => {
    const sku = skuMetaTag?.content

    if (sku) {
        makeThreedViewer(sku)
    }
})

// ------------------------------------------------------------------------------
//      Algolia
// ------------------------------------------------------------------------------

// This sends an algolia product added to cart event through the backend
try {
    const eventData = getAlgoliaEventDataFromLocalStorage()
    const pdp = document.getElementById('catalog-detail-container')!

    if (pdp && eventData) {
        // Making sure the right variant is chosen
        eventData.objectID = pdp.getAttribute('data-variantID')!

        const button = pdp.querySelector('form button')

        if (button) {
            button.addEventListener('click', () => {
                sendAlgoliaEvent(eventData)
            })
        }
    }
} catch (error) {
    bugsnagClient.notify(error)
}
